import { useQuery } from '@tanstack/react-query';
import { AvailableMonolineInsuranceLinesGetResponse, AvailableMonolineInsuranceLinesParams } from '@lio/api-types';
import { useClient } from '@utils/api-client';

export type TPolicyMonolineInsuranceLine = AvailableMonolineInsuranceLinesGetResponse['items'][number];

const config = {
  staleTime: 60000,
};

export const PolicyMonolineInsuranceLineKeys = {
  search: ['policy-monoline-insurance-lines'] as const,
  searchParams: (params: AvailableMonolineInsuranceLinesParams) =>
    [...PolicyMonolineInsuranceLineKeys.search, params] as const,
};

const getQueryParams = (params: AvailableMonolineInsuranceLinesParams) => {
  const { pageLimit, pageOffset } = params;
  const query = new URLSearchParams();

  if (pageLimit) {
    query.append('pageLimit', String(pageLimit));
  }

  if (pageOffset) {
    query.append('pageOffset', String(pageOffset));
  }

  return query.toString();
};

export const useGetAvailablePolicyMonolineInsuranceLines = (
  policyId: string,
  {
    params,
    enabled,
  }: {
    params?: Omit<AvailableMonolineInsuranceLinesParams, 'policyId'>;
    enabled?: boolean;
  },
) => {
  const client = useClient();
  const query = getQueryParams({ ...params, policyId });
  const requestUrl = `policy/${policyId}/available-monoline-insurance-line?${query}`;

  const result = useQuery<any, Error>({
    queryKey: PolicyMonolineInsuranceLineKeys.searchParams({ ...params, policyId }),
    queryFn: async () => {
      const response = await client.get(requestUrl).json<AvailableMonolineInsuranceLinesGetResponse>();
      return response;
    },
    enabled,
    ...config,
  });

  return { ...result, items: result.data?.items as TPolicyMonolineInsuranceLine[] };
};
